import { useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Download, OctagonAlert, ShieldAlert } from "lucide-react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Skeleton } from "../../../ui/skeleton";
import { StatCard } from "../../../components/PortfolioPerformance/stat-card";
import { PAGE_SIZE, Pager } from "../../../components/PortfolioPerformance/pager";
import { FilterBar } from "../../../components/PortfolioPerformance/filter-bar";
import { ApdGridAttestationSection } from "../../../components/PortfolioPerformance/apd-grid-attestation";
import { Button } from "../../../ui/button";
import { useTicketSize } from "../lib/ticket-size";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import {
  useApdMonitoring,
  useSmIndustryPage,
  useSmEquipmentPage,
  useSmRiskClasses,
  useSmSubCategories,
  DEFAULT_SM_SORT,
  type SmSort,
  type SmSortKey,
  APD_DOWNLOAD_TABLES,
  DEFAULT_SM_CLASS,
  downloadApdTable,
} from "../lib/api/apd-monitoring";

function nextSort(prev: SmSort, key: SmSortKey): SmSort {
  if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
  // First click: text columns A to Z, number columns largest first.
  return { key, dir: key === "key" || key === "label" ? "asc" : "desc" };
}

function SortArrow({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) return <ArrowUpDown className="size-3 opacity-40" aria-hidden />;
  return dir === "asc" ? (
    <ArrowUp className="size-3" aria-hidden />
  ) : (
    <ArrowDown className="size-3" aria-hidden />
  );
}

/**
 * Column widths shared by BOTH Special Mention tables (by Industry (SIC) and by Equipment Type), in column
 * order: code, name, APD %, NI, % of Total, 30+ DPD, Del %. Both tables lay out with these same fixed widths, so each
 * column sits at exactly the same x position in both however long a name in either one is, which an automatic
 * layout cannot do (it sizes every column to its own table's content). Both tables must keep these seven columns,
 * in this order.
 */
const SM_COLUMN_WIDTHS = ["10%", "28%", "12.4%", "12.4%", "12.4%", "12.4%", "12.4%"];

function SmTable<T extends Record<string, string>>({
  rows,
  columns,
  sort,
  onSort,
}: {
  rows: T[];
  columns: { key: keyof T; label: string; sort?: SmSortKey; align?: "left" | "right" }[];
  sort: SmSort;
  onSort: (key: SmSortKey) => void;
}) {
  return (
    <table className="w-full table-fixed text-sm">
      <colgroup>
        {columns.map((c, i) => (
          <col key={String(c.key)} style={{ width: SM_COLUMN_WIDTHS[i] }} />
        ))}
      </colgroup>
      <thead>
        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
          {columns.map((c) => {
            const sortKey = c.sort;
            if (!sortKey) {
              return (
                <th key={String(c.key)} className={c.align === "right" ? "pb-2 text-right" : "pb-2"}>
                  {c.label}
                </th>
              );
            }
            const active = sort.key === sortKey;
            return (
              <th
                key={String(c.key)}
                className={c.align === "right" ? "pb-2 text-right" : "pb-2"}
                aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
              >
                <button
                  type="button"
                  onClick={() => onSort(sortKey)}
                  className={
                    "inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-foreground " +
                    (active ? "text-foreground" : "")
                  }
                >
                  {c.label}
                  <SortArrow active={active} dir={sort.dir} />
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-t border-border">
            {columns.map((c) => (
              <td
                key={String(c.key)}
                // Fixed widths: a long name is cut with an ellipsis (full text on hover) instead of
                // wrapping or pushing the other columns out of line with the other table.
                className={c.align === "right" ? "py-2 text-right tabular-nums" : "truncate py-2 pr-3"}
                title={c.align === "right" ? undefined : row[c.key]}
              >
                {row[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const DEFAULT_CLASS = DEFAULT_SM_CLASS;

// The watermark bands behind the Portfolio APD Trend are coloured by the APD grid's CATEGORY: Pass green, Watch amber, and red for
// "Special Mention and Worse" -- which covers Special Mention, Substandard and Doubtful/Loss alike. A category the grid adds later
// is red too (anything past Watch is a worse grade).
function apdBandColor(category: string): string {
  const name = category.trim().toLowerCase();
  if (name === "pass") return "var(--athena-green)";
  if (name === "watch") return "var(--amber)";
  return "var(--decline)";
}

// The Sub Category dropdown's extra first option: every sub-category of the chosen class together (no sub filter).
const ALL_SUB_CATEGORIES = "All";

// One icon per card, mildest to worst; a sub-category the grid adds later falls back to the first.
const SUB_CATEGORY_ICONS: Record<string, typeof AlertTriangle> = {
  "Special Mention": AlertTriangle,
  Substandard: ShieldAlert,
  "Doubtful/Loss": OctagonAlert,
};

export default function ApdMonitoring() {
  const { data, isPending, isError, error } = useApdMonitoring();
  const [industryOffset, setIndustryOffset] = useState(0);
  const [equipmentOffset, setEquipmentOffset] = useState(0);
  // Two linked dropdowns. The class dropdown always lists every category in the APD grid and opens on
  // "Special Mention and Worse" (or the first one if that doesn't exist). The sub-category dropdown lists
  // only the sub-categories mapped to the chosen category, and the moment the category changes it goes back
  // to the first of them (Special Mention, for the default category). The tables wait for both.
  const [pickedClass, setPickedClass] = useState<string | null>(null);
  const [pickedSub, setPickedSub] = useState<string | null>(null);
  const riskClasses = useSmRiskClasses();
  const classOptions = riskClasses.data ?? [];
  const riskClass =
    pickedClass && classOptions.includes(pickedClass)
      ? pickedClass
      : classOptions.includes(DEFAULT_CLASS)
        ? DEFAULT_CLASS
        : classOptions[0];
  const subCategories = useSmSubCategories(riskClass);
  const subOptions = subCategories.data ?? [];
  // "All" is its own pick: no sub filter, so the tables cover every sub-category of the class. Otherwise the pick,
  // or the first sub-category while nothing valid is picked.
  const subIsAll = pickedSub === ALL_SUB_CATEGORIES;
  const subCategory = subIsAll
    ? ALL_SUB_CATEGORIES
    : pickedSub && subOptions.includes(pickedSub)
      ? pickedSub
      : subOptions[0];
  // undefined = list still loading (tables wait); null = no sub filter (this category has no sub-categories, or "All").
  const subFilter = subIsAll ? null : (subCategory ?? (subCategories.isSuccess ? null : undefined));
  // What the table titles name: the sub-category, or the whole class when "All" is picked.
  const scopeLabel = subIsAll ? riskClass : subCategory;
  const [industrySort, setIndustrySort] = useState<SmSort>(DEFAULT_SM_SORT);
  const [equipmentSort, setEquipmentSort] = useState<SmSort>(DEFAULT_SM_SORT);
  const industry = useSmIndustryPage(riskClass, industrySort, subFilter);
  const equipment = useSmEquipmentPage(riskClass, equipmentSort, subFilter);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { ticket } = useTicketSize();

  async function handleDownload(key: string, filename: string) {
    setDownloading(key);
    setDownloadError(null);
    try {
      await downloadApdTable(key, filename, ticket);
    } catch {
      setDownloadError(key);
    } finally {
      setDownloading(null);
    }
  }

  const downloadButton = (key: (typeof APD_DOWNLOAD_TABLES)[number]["key"]) => {
    const t = APD_DOWNLOAD_TABLES.find((x) => x.key === key)!;
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={downloading === t.key}
        onClick={() => handleDownload(t.key, t.filename)}
      >
        <Download className="size-3.5" />
        {downloading === t.key ? "Downloading…" : `Download CSV · ${t.label}`}
      </Button>
    );
  };

  return (
    <div>
      <PageHeader
        title="APD Monitoring"
        subtitle="PayNet-sourced APD score, pulled directly from UDF: trend, then special mention by industry, then by equipment type, as the page scrolls."
        asOfPage="apd-monitoring"
      />

      {downloadError ? (
        <p className="mt-4 text-xs text-destructive">
          Could not download {APD_DOWNLOAD_TABLES.find((t) => t.key === downloadError)?.label}.
        </p>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 px-4 py-3 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load APD monitoring data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending || !data ? (
        <Skeleton className="mt-8 h-64 w-full" />
      ) : (
        <>
        {data.subCategoryCards.length > 0 ? (
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {data.subCategoryCards.map((card) => (
              <StatCard
                key={card.subCategory}
                label={card.subCategory}
                value={card.ni}
                icon={SUB_CATEGORY_ICONS[card.subCategory] ?? AlertTriangle}
                sparkColor="var(--decline)"
                trend={{
                  value: `${card.trend.value} vs previous month${card.comparedTo ? ` (${card.comparedTo})` : ""}`,
                  direction: card.trend.direction,
                  goodDirection: "down",
                }}
                hint={`${card.share} of ${DEFAULT_CLASS} NI`}
              />
            ))}
          </div>
        ) : null}
        <Panel
          className="mt-6"
          title="Portfolio APD Trend - Weighted Average"
          description="NI-weighted average APD across all SICs, last 10 months"
          right={downloadButton("asset-sic-apd")}
        >
          <TrendLineChart
            data={data.trend}
            series={[{ dataKey: "apd", name: "APD %", color: "var(--amber)" }]}
            suffix="%"
            height={260}
            referenceLine={{
              value: data.smThreshold,
              label: `SM threshold ${data.smThreshold}%`,
            }}
            bands={(data.bands ?? []).map((b) => ({
              label: b.label,
              from: b.fromPct,
              to: b.toPct,
              color: apdBandColor(b.category),
            }))}
          />
        </Panel>
        </>
      )}

      <div className="mt-6 flex justify-end">
        <FilterBar
          filters={[
            {
              id: "risk-class",
              label: "Portfolio Risk Class",
              value: riskClass ?? "",
              options: classOptions,
              onChange: (value) => {
                setPickedClass(value);
                setPickedSub(null);
                setIndustryOffset(0);
                setEquipmentOffset(0);
              },
            },
            {
              id: "risk-sub-class",
              label: "Sub Category",
              value: subCategory ?? "",
              options: [ALL_SUB_CATEGORIES, ...subOptions],
              onChange: (value) => {
                setPickedSub(value);
                setIndustryOffset(0);
                setEquipmentOffset(0);
              },
            },
          ]}
        />
      </div>

      <Panel
        className="mt-4"
        title={`${scopeLabel ?? riskClass ?? DEFAULT_CLASS} Summary — by Industry (SIC)`}
        right={downloadButton("sm-sic")}
      >
        {industry.isError ? (
          <p className="text-sm text-destructive">Could not load this table.</p>
        ) : !industry.data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <SmTable
              rows={industry.data.items.slice(industryOffset, industryOffset + PAGE_SIZE)}
              columns={[
                { key: "sic", label: "SIC" },
                { key: "industry", label: "Industry" },
                { key: "apdPct", label: "APD %", sort: "apdPct", align: "right" },
                { key: "ni", label: "NI", sort: "ni", align: "right" },
                { key: "pctOfTotal", label: "% of Total", sort: "pctOfTotal", align: "right" },
                { key: "amount30Plus", label: "30+ DPD", sort: "amount30Plus", align: "right" },
                { key: "delPct", label: "Del %", sort: "delPct", align: "right" },
              ]}
              sort={industrySort}
              onSort={(key) => {
                setIndustrySort((prev) => nextSort(prev, key));
                setIndustryOffset(0);
              }}
            />
            <Pager
              offset={industryOffset}
              total={industry.data.items.length}
              isFetching={industry.isFetching}
              onChange={setIndustryOffset}
            />
          </>
        )}
      </Panel>

      <Panel
        className="mt-6"
        title={`${scopeLabel ?? riskClass ?? DEFAULT_CLASS} Summary — by Equipment Type`}
        right={downloadButton("sm-asset-industry")}
      >
        {equipment.isError ? (
          <p className="text-sm text-destructive">Could not load this table.</p>
        ) : !equipment.data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <SmTable
              rows={equipment.data.items.slice(equipmentOffset, equipmentOffset + PAGE_SIZE)}
              columns={[
                { key: "code", label: "Code" },
                { key: "equipmentType", label: "Equipment Type" },
                { key: "apdPct", label: "APD %", sort: "apdPct", align: "right" },
                { key: "ni", label: "NI", sort: "ni", align: "right" },
                { key: "pctOfTotal", label: "% of Total", sort: "pctOfTotal", align: "right" },
                { key: "amount30Plus", label: "30+ DPD", sort: "amount30Plus", align: "right" },
                { key: "delPct", label: "Del %", sort: "delPct", align: "right" },
              ]}
              sort={equipmentSort}
              onSort={(key) => {
                setEquipmentSort((prev) => nextSort(prev, key));
                setEquipmentOffset(0);
              }}
            />
            <Pager
              offset={equipmentOffset}
              total={equipment.data.items.length}
              isFetching={equipment.isFetching}
              onChange={setEquipmentOffset}
            />
          </>
        )}
      </Panel>

      <ApdGridAttestationSection />
    </div>
  );
}
